import React, { createContext, useContext, useState, useCallback } from 'react';

import { Notification, NotificationType } from './Notification';
import { Notifications } from './Notifications';

interface NotificationsContextValue {
    addNotification: (message: string, type: NotificationType, duration?: number) => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function useNotifications() {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationsProvider');
    }
    return context;
}

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const removeNotification = useCallback((id: string) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, []);
    const addNotification = useCallback((message: string, type: NotificationType, duration = 3000) => {
        const id = crypto.randomUUID();
        const newNotification: Notification = { id, message, type, duration };
        setNotifications((prev) => [...prev, newNotification]);
        if (duration > 0) {
            setTimeout(() => removeNotification(id), duration);
        }
    }, [removeNotification]);
    return (
        <NotificationsContext.Provider value={{ addNotification }}>
            {children}
            <Notifications notifications={notifications} onRemove={removeNotification} />
        </NotificationsContext.Provider>
    );
};
